/**
 * TALLER — el puente entre tres formas distintas de pensar el mismo mueble.
 * =============================================================================
 *
 * Este archivo existe por una petición de Juan del 2026-08-15: quería poder **capturar
 * los valores de su taller y corregirlos después contra la realidad**, y pidió
 * explícitamente que la lógica saliera de juntar tres perspectivas — la de quien levanta,
 * la del cliente y la del fabricante.
 *
 * Investigando cómo cotiza el oficio, esas tres perspectivas resultaron ser exactamente
 * los tres métodos que se usan en el mundo, y la fricción entre ellos es el problema
 * central del rubro.
 *
 * ---------------------------------------------------------------------------
 * LAS TRES CABEZAS, Y POR QUÉ NINGUNA SOBRA
 *
 * **1. El cliente piensa en METROS LINEALES.** Es como pide presupuesto y como compara:
 * «¿a cuánto me sale el metro de cocina?». No va a entender una lista de placas, y pedirle
 * que la entienda es perder la venta contra el que sí le dio un número por metro.
 *
 * **2. Quien levanta piensa en MÓDULOS.** Un mueble bajo de 90 con dos cajones. Eso lo
 * puede contar cualquiera parado en la cocina, sin saber carpintería — que es el
 * Principio 7 de `PRODUCT.md` aplicado a este rubro.
 *
 * **3. El fabricante piensa en COMPONENTES.** Placas enteras, metros de corte, metros de
 * canto, herrajes, minutos de máquina. Es lo único que de verdad cuesta dinero, y es lo
 * que `fabricacion.js` ya calcula bien.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ NO BASTA CON EL METRO LINEAL, QUE ES LO QUE HACE TODO EL MERCADO
 *
 * `FUENTE:` foro profesional de WoodWeb, *«Fine Points of Linear-Foot Pricing»* y
 * *«Methods for Pricing Cabinet Work»*. La crítica que más se repite, textual:
 *
 *   > *«nada es evidente. ¿Qué es un pie de gabinetes?»*
 *
 * Las ambigüedades que enumeran los profesionales son las mismas que arruinan una
 * cotización: si se mide el muro o el frente, qué pasa con los muebles de esquina, si el
 * bloque de cajones cuenta igual que una puerta, y qué incluye el precio base. Y la
 * consecuencia la dicen sin rodeos: *«si cotizas por pie lineal vas a cotizar de menos
 * unos trabajos y de más otros»*, porque el método promedia costos que no son parejos.
 *
 * **La solución que este archivo implementa es la que recomiendan ellos mismos:** calcular
 * por componentes y *presentar* por metro lineal. El número que ve el cliente es el que
 * entiende; el número que sostiene el precio es el que cuesta. Y la pantalla muestra los
 * dos, con lo que el metro lineal incluye y lo que no — porque esa lista es justo la que
 * falta cuando dos presupuestos «por metro» no se parecen.
 * ============================================================================= */

import { calcularFabricacion, MATERIALES_BASE, PROCESOS_BASE, HERRAJES_BASE, APROVECHAMIENTO_BASE } from './fabricacion.js'

/* ============================================================================
   1. CONFIGURACIÓN DEL TALLER — lo que Juan captura y corrige
   ========================================================================== */

/**
 * Todo lo que gobierna un costo de taller, junto y en un solo lugar, para que la pantalla
 * de configuración tenga qué editar.
 *
 * ⚠️ **Hasta hoy esto era mentira.** `fabricacion.js` recibía un parámetro `cfg` para
 * sobrescribir sus constantes y su comentario prometía que «cada uno se corrige desde la
 * pantalla de Configuración sin tocar código» — pero las dos llamadas del sistema pasaban
 * `{}`. Los valores eran constantes en la práctica. Es el mismo tipo de hueco que el
 * botón de «Agregar a la cotización» que existía y nunca se conectaba.
 *
 * `sinVerificar` en bloque, y no por renglón, porque **ninguno de estos números salió del
 * taller de Juan**: son órdenes de magnitud para que la interfaz exista y se pueda
 * corregir. El primer mueble real los sustituye todos.
 */
export const TALLER_BASE = {
  sinVerificar: true,
  materiales: MATERIALES_BASE,
  procesos: PROCESOS_BASE,
  herrajes: HERRAJES_BASE,
  aprovechamiento: APROVECHAMIENTO_BASE
}

/**
 * HALLAZGO DEL 2026-08-15 QUE VALE COMO ARGUMENTO DE NEGOCIO, no sólo como nota técnica.
 *
 * Al probar que editar el aprovechamiento cambiara el costo, **la prueba falló y la
 * equivocada era la prueba**: en una cocina de cinco muebles, subir el aprovechamiento de
 * 0.78 a 0.90 no ahorra ni un peso.
 *
 * La razón es que **las placas se compran enteras**. Un anidado mejor no ahorra nada hasta
 * que ahorra una placa completa, y en un trabajo chico el redondeo se traga la mejora. En
 * la misma cocina multiplicada por cuatro, el mismo cambio ahorra cinco placas.
 *
 * Lo que esto significa para el negocio, y no es obvio:
 *
 *   · **Invertir en mejorar el anidado paga en volumen, no en el mueble suelto.** Un taller
 *     que hace cocinas completas lo recupera; uno que hace repisas sueltas, no.
 *   · **En un trabajo chico, el desperdicio ya está pagado.** Conviene usarlo: sacar de esa
 *     misma placa una repisa extra o una pieza de muestra sale gratis.
 *   · Y al revés: prometerle a un cliente un descuento «porque optimizamos el corte» en un
 *     trabajo de tres muebles es regalar margen sin haber ahorrado nada.
 */
export const ANIDADO_PAGA_EN_VOLUMEN =
  'Mejorar el aprovechamiento no baja el costo hasta que ahorra una placa entera. En ' +
  'trabajos chicos el desperdicio ya está pagado: conviene aprovecharlo, no descontarlo.'

/**
 * Qué incluye y qué no el precio por metro lineal.
 *
 * **Esta lista no es un descargo legal: es la herramienta de venta del rubro.** Cuando un
 * cliente compara dos presupuestos «por metro» y uno es 40% más barato, casi siempre la
 * diferencia está aquí y no en el mueble. Poder enseñar la lista convierte una discusión
 * de precio en una de alcance, que es la única que se puede ganar.
 *
 * `FUENTE:` lo que los profesionales de WoodWeb reportan cobrar aparte — acabado por
 * superficie, instalación, herrajes premium, molduras y accesorios especiales.
 */
export const ALCANCE_METRO_LINEAL = {
  incluye: [
    'Caja del mueble en el material cotizado, con canto en los bordes visibles',
    'Puertas y cajones lisos del mismo material',
    'Herrajes estándar: bisagras, correderas y tiradores de línea',
    'Corte, canteado y armado en taller'
  ],
  noIncluye: [
    'Instalación en sitio, que se cotiza aparte por mueble',
    'Cubierta, tarja, mezcladora y electrodomésticos',
    'Acabados especiales: laca, poliuretano, chapa de madera natural',
    'Herrajes premium: cierre suave, esquineros giratorios, botes de basura integrados',
    'Cualquier ajuste por muro fuera de escuadra, que se mide en sitio'
  ]
}

/* ============================================================================
   2. MÓDULOS — el lenguaje de quien levanta
   ========================================================================== */

/**
 * CATÁLOGO DE MÓDULOS PARAMÉTRICOS.
 *
 * Cada uno sabe **generar su propio despiece** a partir de sus medidas. Es la pieza que
 * faltaba: hasta hoy los despieces eran fijos —tres productos de muestra escritos a mano—
 * y un mueble de 90 no se podía cotizar si el despiece estaba escrito para 60.
 *
 * `ancho`, `alto` y `fondo` van en **milímetros**, que es como se trabaja en carpintería y
 * como los pide `fabricacion.js`. La interfaz los captura en centímetros porque es como
 * habla la gente, y los convierte.
 *
 * **Convención de armado**, y hay que fijarla porque cambia el despiece: caja de costados
 * enteros —los laterales corren de piso a cubierta y el piso y la cubierta van entre
 * ellos—, respaldo de 6 mm ranurado, y frentes sobrepuestos. Es la construcción más común
 * en cocina de MDF en México. Si el taller de Juan arma distinto, esto se corrige aquí y
 * todo el catálogo cambia con ello.
 */
export const MODULOS = [
  {
    id: 'bajo-puertas',
    nombre: 'Mueble bajo con puertas',
    familia: 'cocina',
    ancho: 900, alto: 850, fondo: 600,
    que: 'La base de una cocina. Puertas abatibles y un entrepaño adentro.',
    paraElCliente: 'Guardado profundo para ollas y despensa.',
    despiece: m => cajaBase(m, { puertas: m.ancho > 600 ? 2 : 1, cajones: 0, entrepanos: 1 })
  },
  {
    id: 'bajo-cajones',
    nombre: 'Mueble bajo de cajones',
    familia: 'cocina',
    ancho: 600, alto: 850, fondo: 600,
    que: 'Tres o cuatro cajones en toda la altura.',
    paraElCliente: 'Lo que de verdad se usa a diario. Cuesta más y se agradece más.',
    despiece: m => cajaBase(m, { puertas: 0, cajones: 3, entrepanos: 0 })
  },
  {
    id: 'bajo-mixto',
    nombre: 'Mueble bajo mixto',
    familia: 'cocina',
    ancho: 600, alto: 850, fondo: 600,
    que: 'Un cajón arriba y puerta abajo.',
    paraElCliente: 'El compromiso más común: cubiertos arriba, ollas abajo.',
    despiece: m => cajaBase(m, { puertas: 1, cajones: 1, entrepanos: 1 })
  },
  {
    id: 'alto',
    nombre: 'Mueble alto',
    familia: 'cocina',
    ancho: 900, alto: 700, fondo: 350,
    que: 'Colgado sobre la cubierta, con puertas y entrepaños.',
    paraElCliente: 'Guardado de vajilla y despensa seca, a la mano.',
    despiece: m => cajaBase(m, { puertas: m.ancho > 600 ? 2 : 1, cajones: 0, entrepanos: 2 })
  },
  {
    id: 'alacena',
    nombre: 'Alacena de piso a techo',
    familia: 'cocina',
    ancho: 600, alto: 2200, fondo: 600,
    que: 'Columna completa con entrepaños.',
    paraElCliente: 'Donde cabe la despensa entera sin apilar.',
    despiece: m => cajaBase(m, { puertas: 2, cajones: 0, entrepanos: 4 })
  },
  {
    id: 'closet-modulo',
    nombre: 'Módulo de clóset',
    familia: 'closet',
    ancho: 900, alto: 2200, fondo: 550,
    que: 'Columna con tubo para colgar y entrepaños.',
    paraElCliente: 'Se combina con cajoneras para armar el clóset completo.',
    despiece: m => cajaBase(m, { puertas: 0, cajones: 0, entrepanos: 2 })
  },
  {
    id: 'closet-cajonera',
    nombre: 'Cajonera de clóset',
    familia: 'closet',
    ancho: 600, alto: 900, fondo: 500,
    que: 'Cuatro cajones, va dentro del clóset.',
    paraElCliente: 'Para lo que no se cuelga.',
    despiece: m => cajaBase(m, { puertas: 0, cajones: 4, entrepanos: 0 })
  },
  {
    id: 'repisa',
    nombre: 'Repisa o entrepaño flotante',
    familia: 'otros',
    ancho: 1200, alto: 40, fondo: 250,
    que: 'Tabla sola, sin caja.',
    paraElCliente: 'Lo más barato por metro, y lo que menos guarda.',
    despiece: m => ({
      piezas: [{ nombre: 'Repisa', material: 'MDF18', largo: m.ancho, ancho: m.fondo, cant: 1, cantoLargo: 2, cantoAncho: 2 }],
      herrajes: [{ sku: 'HER-TAQUETE', cant: 4 }],
      procesos: [{ proceso: 'armado', minutos: 12 }]
    })
  }
]

export const moduloPorId = id => MODULOS.find(m => m.id === id) || MODULOS[0]

/**
 * Genera el despiece de una caja estándar.
 *
 * Lo que un carpintero hace de memoria y quien levanta no tiene por qué saber. Va aquí, en
 * un solo lugar, y con las restas explicadas: son las que se equivocan siempre.
 */
function cajaBase (m, { puertas = 0, cajones = 0, entrepanos = 0 }) {
  const ESP = 18          // espesor de la placa estructural, en mm
  const HOLGURA = 3       // juego total entre frentes, en mm

  /* Costados enteros: corren toda la altura. Piso y cubierta van ENTRE ellos, así que
     miden el ancho menos dos espesores. Ésta es la resta que más se equivoca, y de ella
     depende que el mueble entre en el hueco. */
  const anchoInterior = m.ancho - 2 * ESP

  const piezas = [
    { nombre: 'Costado', material: 'MDF18', largo: m.alto, ancho: m.fondo, cant: 2, cantoLargo: 1 },
    { nombre: 'Piso', material: 'MDF18', largo: anchoInterior, ancho: m.fondo, cant: 1, cantoLargo: 1 },
    { nombre: 'Cubierta', material: 'MDF18', largo: anchoInterior, ancho: m.fondo, cant: 1, cantoLargo: 1 },
    /* Respaldo de 6 mm. Va del tamaño exterior porque se ranura en los costados. */
    { nombre: 'Respaldo', material: 'MDF6', largo: m.alto, ancho: m.ancho, cant: 1 }
  ]

  if (entrepanos > 0) {
    /* El entrepaño va 20 mm más corto en fondo para que no tope con el respaldo. */
    piezas.push({
      nombre: 'Entrepaño', material: 'MDF18',
      largo: anchoInterior, ancho: m.fondo - 20, cant: entrepanos, cantoLargo: 1
    })
  }

  const herrajes = [
    { sku: 'HER-TORNILLO', cant: 16 + entrepanos * 4 },
    { sku: 'HER-TARUGO', cant: 8 }
  ]
  const procesos = []

  if (puertas > 0) {
    const anchoPuerta = Math.round((m.ancho - HOLGURA * puertas) / puertas)
    /* Con cajones arriba, la puerta ocupa lo que sobra de la altura. */
    const altoPuerta = cajones > 0 ? m.alto - cajones * 200 : m.alto
    piezas.push({
      nombre: 'Puerta', material: 'MDF18',
      largo: altoPuerta, ancho: anchoPuerta, cant: puertas, cantoLargo: 2, cantoAncho: 2
    })
    /* Dos bisagras por puerta hasta 900 mm de alto, tres arriba de eso. Es la regla de
       taller: una puerta alta con dos bisagras se pandea con el tiempo. */
    herrajes.push({ sku: 'HER-BISAGRA', cant: puertas * (altoPuerta > 900 ? 3 : 2) })
    herrajes.push({ sku: 'HER-TIRADOR', cant: puertas })
  }

  if (cajones > 0) {
    const altoFrente = Math.round((cajones > 0 && puertas > 0 ? cajones * 200 : m.alto) / cajones) - HOLGURA
    piezas.push({
      nombre: 'Frente de cajón', material: 'MDF18',
      largo: altoFrente, ancho: m.ancho - HOLGURA, cant: cajones, cantoLargo: 2, cantoAncho: 2
    })
    /* La caja del cajón: dos costados, frente, trasera y fondo de 6 mm. */
    piezas.push({
      nombre: 'Costado de cajón', material: 'MDF18',
      largo: m.fondo - 50, ancho: 150, cant: cajones * 2, cantoLargo: 1
    })
    piezas.push({
      nombre: 'Frente y trasera de cajón', material: 'MDF18',
      largo: anchoInterior - 30, ancho: 150, cant: cajones * 2, cantoLargo: 1
    })
    piezas.push({
      nombre: 'Fondo de cajón', material: 'MDF6',
      largo: m.fondo - 60, ancho: anchoInterior - 30, cant: cajones
    })
    herrajes.push({ sku: 'HER-CORREDERA45', cant: cajones })
    herrajes.push({ sku: 'HER-TIRADOR', cant: cajones })
    herrajes.push({ sku: 'HER-TORNILLO', cant: cajones * 12 })
  }

  /* El armado crece con la complejidad, no con el tamaño: un cajón cuesta más minutos de
     banco que un entrepaño, y una puerta más que nada. */
  procesos.push({ proceso: 'armado', minutos: 25 + cajones * 18 + puertas * 10 + entrepanos * 5 })
  if (m.alto > 1500) procesos.push({ proceso: 'armado', minutos: 15 })   // una columna se arma de pie

  return { piezas, herrajes, procesos }
}

/* ============================================================================
   3. EL PUENTE — de módulos a costo, y de costo a metro lineal
   ========================================================================== */

/**
 * Calcula un conjunto de módulos: el costo real por componentes y su traducción a metro
 * lineal, que es lo que ve el cliente.
 *
 * `metroLineal` se mide sobre el **frente** del mueble, no sobre el muro, y hay que
 * decirlo: es la primera ambigüedad que denuncian los profesionales. Un mueble de esquina
 * ocupa más muro que frente, y quien mide el muro cobra de más sin querer.
 *
 * **Los muebles altos y bajos se cuentan por separado**, y ésa es la segunda ambigüedad.
 * Un metro de cocina con mueble bajo y alto tiene dos muebles, no uno. Promediarlos en un
 * solo «metro de cocina» es exactamente lo que hace que dos presupuestos por metro no se
 * puedan comparar.
 */
/**
 * Holgura mínima total entre el conjunto de muebles y el hueco, en milímetros.
 *
 * `sinVerificar:` es criterio de taller, no norma. 10 mm es lo que se usa habitualmente
 * para un hueco entre dos muros; si Juan trabaja con otra tolerancia, se cambia aquí.
 */
export const HOLGURA_HUECO = { mm: 10, sinVerificar: true }

/**
 * Diferencia entre la medida mayor y la menor del hueco a partir de la cual el mueble
 * necesita un suplemento de ajuste, que se cotiza aparte.
 */
export const DESPLOME_TOLERABLE = { mm: 15, sinVerificar: true }

/**
 * REVISA EL HUECO CONTRA LOS MÓDULOS — la comprobación que evita el error caro del rubro.
 *
 * En eléctrico un error de dimensionamiento se corrige cambiando un cable. Aquí no: la
 * placa ya se cortó, y un mueble que no entra se vuelve a fabricar. Por eso esta función
 * existe y por eso la guía pide medir en tres alturas.
 *
 * Dos cosas distintas se revisan, y conviene no confundirlas:
 *
 *   1. **Que quepa.** La suma de los frentes contra la medida MENOR del hueco, no contra
 *      el promedio ni contra la que se midió primero. La menor es la que tiene que pasar.
 *   2. **Que el muro esté derecho.** La diferencia entre la mayor y la menor. Si es
 *      grande, el mueble entra pero deja una cuña visible, y eso pide un suplemento que
 *      **se cotiza, no se absorbe**.
 *
 * `medidas` son los tres anchos medidos, en milímetros. Con uno solo también funciona,
 * pero entonces no puede advertir del desplome y lo dice.
 */
export function revisarHueco (medidas = [], metrosFrente = 0) {
  const validas = medidas.filter(m => m > 0)
  if (!validas.length) {
    return { hayHueco: false, porque: 'No se capturó la medida del hueco.' }
  }

  const menor = Math.min(...validas)
  const mayor = Math.max(...validas)
  const desplome = mayor - menor
  const frenteMm = metrosFrente * 1000
  const sobra = menor - frenteMm

  return {
    hayHueco: true,
    menor, mayor, desplome,
    frenteMm,
    sobra,
    /** ¿Cabe con la holgura mínima? */
    cabe: sobra >= HOLGURA_HUECO.mm,
    /** ¿El muro está lo bastante derecho para no pedir suplemento? */
    derecho: validas.length > 1 ? desplome <= DESPLOME_TOLERABLE.mm : null,
    unaSolaMedida: validas.length === 1,
    porque: sobra < 0
      ? `Los muebles suman ${Math.round(frenteMm)} mm de frente y el hueco más angosto mide ` +
        `${menor} mm. **No entran por ${Math.abs(Math.round(sobra))} mm.**`
      : sobra < HOLGURA_HUECO.mm
        ? `Entran justos: sobran ${Math.round(sobra)} mm y se recomiendan al menos ` +
          `${HOLGURA_HUECO.mm}. Sin holgura, el último mueble entra a la fuerza.`
        : `Entran con ${Math.round(sobra)} mm de holgura sobre la medida más angosta.`,
    porqueDesplome: validas.length === 1
      ? 'Sólo se capturó una medida del hueco, así que no se puede saber si el muro está ' +
        'derecho. Conviene medir arriba, a la mitad y abajo.'
      : desplome <= DESPLOME_TOLERABLE.mm
        ? `El muro varía ${desplome} mm entre su punto más ancho y el más angosto, que está ` +
          'dentro de lo que absorbe un zoclo normal.'
        : `El muro varía ${desplome} mm, más de los ${DESPLOME_TOLERABLE.mm} que absorbe un ` +
          'zoclo normal. Hace falta un suplemento de ajuste, y eso se cotiza aparte.'
  }
}

export function calcularTaller ({ modulos = [], cfg = TALLER_BASE, hueco = [] }) {
  const detalle = modulos.map(m => {
    const def = moduloPorId(m.moduloId)
    const medidas = {
      ancho: m.ancho || def.ancho,
      alto: m.alto || def.alto,
      fondo: m.fondo || def.fondo
    }
    const despiece = def.despiece(medidas)
    const fab = calcularFabricacion(despiece, cfg, m.cantidad || 1)
    return {
      modulo: def, medidas,
      cantidad: m.cantidad || 1,
      despiece, fab,
      /** Metro lineal de frente que ocupa este renglón. */
      metrosFrente: (medidas.ancho / 1000) * (m.cantidad || 1),
      costo: fab.costoTotal * (m.cantidad || 1)
    }
  })

  const costoTotal = detalle.reduce((s, d) => s + d.costo, 0)

  /* Frente por familia, no todo revuelto. Un metro de bajos y un metro de altos cuestan
     distinto, y presentarlos juntos es lo que vuelve incomparables los presupuestos. */
  const porFamilia = {}
  for (const d of detalle) {
    const f = d.modulo.familia
    if (!porFamilia[f]) porFamilia[f] = { metros: 0, costo: 0, modulos: 0 }
    porFamilia[f].metros += d.metrosFrente
    porFamilia[f].costo += d.costo
    porFamilia[f].modulos += d.cantidad
  }
  for (const f of Object.values(porFamilia)) {
    f.costoPorMetro = f.metros > 0 ? f.costo / f.metros : 0
  }

  const metrosTotal = detalle.reduce((s, d) => s + d.metrosFrente, 0)

  return {
    detalle,
    costoTotal,
    metrosTotal,
    costoPorMetro: metrosTotal > 0 ? costoTotal / metrosTotal : 0,
    porFamilia,
    alcance: ALCANCE_METRO_LINEAL,
    /** La comprobación de que lo diseñado entra en el hueco medido. */
    hueco: revisarHueco(hueco, metrosTotal),
    /** Placas enteras por material, sumadas de todos los módulos. Es lo que se compra. */
    placas: placasTotales(detalle),
    /* La advertencia que hace comparable el número. Sin ella, el costo por metro se lee
       como precio de mercado y no lo es: es COSTO, sin utilidad ni IVA. */
    avisoMetro:
      'Este costo por metro es de fabricación, sin utilidad ni IVA, y se mide sobre el ' +
      'FRENTE del mueble, no sobre el muro. Los muebles altos y bajos se cuentan por ' +
      'separado porque no cuestan lo mismo: un metro de cocina con bajo y alto son dos ' +
      'metros de mueble.',
    sinVerificar: cfg.sinVerificar !== false
  }
}

/** Agrega las placas de todos los módulos: es lo que se va a comprar de verdad. */
function placasTotales (detalle) {
  const acc = {}
  for (const d of detalle) {
    for (const m of d.fab.materiales || []) {
      if (!acc[m.clave]) acc[m.clave] = { nombre: m.nombre, placas: 0, costo: 0 }
      acc[m.clave].placas += m.placasPedido
      acc[m.clave].costo += m.costo * d.cantidad
    }
  }
  return acc
}
